import { Concept } from '@/types';

const STATIC_CONCEPTS: Record<string, Concept> = {
    'version-control': {
        id: 'version-control',
        title: 'Version Control',
        tags: ['basics', 'vcs', 'history'],
        summary: 'A system that tracks all changes to files over time — like save points for code.',
        related: ['commits', 'repositories', 'branching']
    },
    'repositories': {
        id: 'repositories',
        title: 'Repositories',
        tags: ['storage', 'git', 'folders'],
        summary: 'A folder where Git tracks all your project changes, stored in a hidden .git directory.',
        related: ['version-control', 'commits']
    },
    'commits': {
        id: 'commits',
        title: 'Commits',
        tags: ['save', 'snapshot', 'history'],
        summary: 'A snapshot of your files at a specific point in time. Each commit has a message and a unique ID.',
        related: ['version-control', 'repositories', 'staging-area']
    },
    'staging-area': {
        id: 'staging-area',
        title: 'Staging Area',
        tags: ['inventory', 'prep', 'selection'],
        summary: 'The middle ground between your workspace and your history. Think of it as your inventory before you save.',
        related: ['commits', 'git-add']
    },
    'branching': {
        id: 'branching',
        title: 'Branching',
        tags: ['parallel', 'multiverse', 'features'],
        summary: 'Creating parallel versions of your project. Master complex features without breaking the main game.',
        related: ['merging', 'pull-requests']
    },
    'merging': {
        id: 'merging',
        title: 'Merging',
        tags: ['combine', 'sync', 'unite'],
        summary: 'Combining changes from one branch back into another, like merging two save files into one.',
        related: ['branching', 'merge-conflicts']
    },
    'merge-conflicts': {
        id: 'merge-conflicts',
        title: 'Merge Conflicts',
        tags: ['collision', 'error', 'resolution'],
        summary: 'When Git can’t automatically merge changes because two people edited the same line.',
        related: ['merging']
    },
    'remote-repos': {
        id: 'remote-repos',
        title: 'Remote Repositories',
        tags: ['cloud', 'backup', 'collaborate'],
        summary: 'Replying your repository to a server like GitHub so others can access it.',
        related: ['github', 'push-pull']
    },
    'git-config': {
        id: 'git-config',
        title: 'Git Config',
        tags: ['git', 'config', 'setup', 'global', 'identity'],
        summary: 'How you tell Git who you are — your name and email stamp on every commit.',
        related: ['commits', 'repositories']
    },
    'gitignore': {
        id: 'gitignore',
        title: '.gitignore',
        tags: ['git', 'ignore', 'exclude', 'secrets', 'node_modules'],
        summary: 'A file that tells Git which files to completely ignore (like secrets and massive node_modules).',
        related: ['repositories', 'version-control']
    },
    'pull-requests': {
        id: 'pull-requests',
        title: 'Pull Requests',
        tags: ['git', 'github', 'pr', 'code-review', 'collaboration', 'merge'],
        summary: 'A request to merge your changes into another branch, enabling team review before code ships.',
        related: ['branching', 'merging', 'remote-repos']
    }
};

const conceptMarkdownFiles = import.meta.glob('/content/concepts/*.md', {
    eager: true,
    query: '?raw',
    import: 'default'
}) as Record<string, string>;

function parseArrayValue(rawValue?: string): string[] {
    if (!rawValue) {
        return [];
    }

    const trimmed = rawValue.trim();
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
        return [];
    }

    return trimmed
        .slice(1, -1)
        .split(',')
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
}

function toTitleFromId(id: string): string {
    return id
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function extractSummary(markdown: string): string {
    const lines = markdown
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .filter((line) => !line.startsWith('#'));

    const summaryLine = lines.find((line) => !line.startsWith('```') && !line.startsWith('- '));
    if (!summaryLine) {
        return 'Concept briefing is loading from the knowledge vault.';
    }

    return summaryLine.replace(/[*_`]/g, '').slice(0, 180);
}

function parseConceptMarkdown(filePath: string, markdown: string): Concept {
    const fileName = filePath.split('/').pop() ?? '';
    const fallbackId = fileName.replace('.md', '');

    const parts = markdown.split('---');
    const hasFrontmatter = markdown.startsWith('---') && parts.length >= 3;

    const frontmatter = hasFrontmatter ? parts[1] : '';
    const body = hasFrontmatter ? parts.slice(2).join('---').trim() : markdown;

    const idMatch = frontmatter.match(/^id:\s*(.+)$/m);
    const titleMatch = frontmatter.match(/^title:\s*(.+)$/m);
    const tagsMatch = frontmatter.match(/^tags:\s*(.+)$/m);
    const conceptsCoveredMatch = frontmatter.match(/^concepts_covered:\s*(.+)$/m);
    const relatedMatch = frontmatter.match(/^related_concepts:\s*(.+)$/m);

    const id = (idMatch?.[1]?.trim() ?? fallbackId).replace(/^['"]|['"]$/g, '');
    const titleRaw = titleMatch?.[1]?.trim();
    const title = titleRaw ? titleRaw.replace(/^['"]|['"]$/g, '') : toTitleFromId(id);

    const tags = parseArrayValue(tagsMatch?.[1])
        .concat(parseArrayValue(conceptsCoveredMatch?.[1]))
        .filter((value, index, all) => all.indexOf(value) === index);

    return {
        id,
        title,
        tags,
        related: parseArrayValue(relatedMatch?.[1]),
        summary: extractSummary(body)
    };
}

const MARKDOWN_CONCEPTS: Record<string, Concept> = {};

Object.entries(conceptMarkdownFiles).forEach(([filePath, markdown]) => {
    const concept = parseConceptMarkdown(filePath, markdown);
    MARKDOWN_CONCEPTS[concept.id] = concept;
});

export const CONCEPTS: Record<string, Concept> = {
    ...MARKDOWN_CONCEPTS,
    ...STATIC_CONCEPTS
};

export const getConceptById = (id: string): Concept | undefined => {
    const normalized = decodeURIComponent(id).trim().toLowerCase();
    return CONCEPTS[normalized];
};
